import { Alert, Button, Card, Field, Input, Modal, Select } from "@aman/design-system";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, asApiError, type ApiError } from "../api/client";
import { useApiQuery } from "../api/hooks";
import { ApiErrorAlert, DataTable, LoadingTable, PageHeader, StatusBadge } from "../components/common";
import { listFromPayload, objectFromPayload, stringValue, type AnyRecord } from "../types";

export function VouchersPage() {
  const { t } = useTranslation();
  const denominations = useApiQuery<unknown>("/voucher-denominations");
  const batches = useApiQuery<unknown>("/voucher-batches");
  const [denominationOpen, setDenominationOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError>();
  const [message, setMessage] = useState<string>();

  const reload = async (): Promise<void> => {
    await Promise.all([denominations.reload(), batches.reload()]);
  };
  const download = async (batch: AnyRecord): Promise<void> => {
    setBusy(true); setError(undefined);
    try {
      const publicId = stringValue(batch["publicId"]);
      const blob = await api.get<Blob>(`/voucher-batches/${encodeURIComponent(publicId)}/pdf`, {
        raw: true, headers: { Accept: "application/pdf" },
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = `${publicId}.pdf`; anchor.click();
      URL.revokeObjectURL(url);
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };
  const toggleBatch = async (batch: AnyRecord): Promise<void> => {
    const status = stringValue(batch["status"]) === "active" ? "disabled" : "active";
    setBusy(true); setError(undefined); setMessage(undefined);
    try {
      await api.post(`/voucher-batches/${encodeURIComponent(stringValue(batch["publicId"]))}/status`, { status });
      setMessage(t("common.success"));
      await batches.reload();
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };

  return <div className="page-stack">
    <PageHeader title={t("pages.vouchers.title")} subtitle={t("pages.vouchers.subtitle")} actions={<div className="table-actions">
      <Button type="button" variant="secondary" onClick={() => setDenominationOpen(true)}>＋ {t("pages.vouchers.addDenomination")}</Button>
      <Button type="button" onClick={() => setBatchOpen(true)}>＋ {t("pages.vouchers.createBatch")}</Button>
    </div>} />
    <ApiErrorAlert error={denominations.error ?? batches.error ?? error} onRetry={() => void reload()} />
    {message !== undefined ? <Alert tone="success">{message}</Alert> : null}
    <Card title={t("pages.vouchers.denominations")} bodyClassName="card-body--flush">
      {denominations.loading ? <LoadingTable columns={4} /> : <DataTable rows={listFromPayload(denominations.data)} columns={[
        { key: "publicId", label: "ID", render: (row) => <code>{stringValue(row["publicId"])}</code> },
        { key: "name", label: t("common.name") },
        { key: "credits", label: t("pages.vouchers.credits") },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
      ]} />}
    </Card>
    <Card title={t("pages.vouchers.batches")} description={t("pages.vouchers.pdfDescription")} bodyClassName="card-body--flush">
      {batches.loading ? <LoadingTable columns={8} /> : <DataTable rows={listFromPayload(batches.data)} columns={[
        { key: "publicId", label: "ID", render: (row) => <code>{stringValue(row["publicId"])}</code> },
        { key: "denominationName", label: t("pages.vouchers.denomination") },
        { key: "credits", label: t("pages.vouchers.credits") },
        { key: "quantity", label: t("pages.vouchers.quantity") },
        { key: "active", label: t("common.active") },
        { key: "redeemed", label: t("pages.vouchers.used") },
        { key: "status", label: t("common.status"), render: (row) => <StatusBadge value={row["status"]} /> },
        { key: "createdAt", label: t("common.date") },
      ]} actions={(row) => <>
        <Button type="button" size="small" variant="secondary" busy={busy} onClick={() => void download(row)}>{t("pages.vouchers.downloadPdf")}</Button>
        {["active", "disabled"].includes(stringValue(row["status"])) ? <Button type="button" size="small"
          variant={stringValue(row["status"]) === "active" ? "danger" : "secondary"} busy={busy}
          onClick={() => void toggleBatch(row)}>{stringValue(row["status"]) === "active" ? t("pages.vouchers.disable") : t("pages.vouchers.enable")}</Button> : null}
      </>} />}
    </Card>
    <DenominationModal open={denominationOpen} busy={busy} error={error} onClose={() => setDenominationOpen(false)}
      onCreated={() => { setDenominationOpen(false); setMessage(t("pages.vouchers.denominationCreated")); void denominations.reload(); }}
      setBusy={setBusy} setError={setError} />
    <BatchModal open={batchOpen} busy={busy} error={error} denominations={listFromPayload(denominations.data)}
      onClose={() => setBatchOpen(false)} onCreated={(created) => { setBatchOpen(false); setMessage(t("pages.vouchers.batchCreated")); void batches.reload(); void download(created); }}
      setBusy={setBusy} setError={setError} />
  </div>;
}

interface ModalStateProps {
  open: boolean; busy: boolean; error: ApiError | undefined; onClose: () => void;
  setBusy: (value: boolean) => void; setError: (value: ApiError | undefined) => void;
}

function DenominationModal({ open, busy, error, onClose, onCreated, setBusy, setError }: ModalStateProps & { onCreated: () => void }) {
  const { t } = useTranslation(); const [name, setName] = useState(""); const [credits, setCredits] = useState("");
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setError(undefined);
    try { await api.post("/voucher-denominations", { name, credits }); setName(""); setCredits(""); onCreated(); }
    catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };
  return <Modal open={open} title={t("pages.vouchers.addDenomination")} onClose={onClose}><ApiErrorAlert error={error} /><form className="form-grid" onSubmit={(event) => void submit(event)}>
    <Field label={t("common.name")}><Input required maxLength={200} value={name} onChange={(event) => setName(event.target.value)} /></Field>
    <Field label={t("pages.vouchers.credits")}><Input required type="number" min="1" step="1" value={credits} onChange={(event) => setCredits(event.target.value)} /></Field>
    <Button type="submit" busy={busy}>{t("common.create")}</Button>
  </form></Modal>;
}

function BatchModal({ open, busy, error, denominations, onClose, onCreated, setBusy, setError }: ModalStateProps & {
  denominations: AnyRecord[]; onCreated: (created: AnyRecord) => void;
}) {
  const { t } = useTranslation(); const [denominationId, setDenominationId] = useState(""); const [quantity, setQuantity] = useState("");
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setError(undefined);
    try {
      const created = objectFromPayload(await api.post<unknown>("/voucher-batches", { denominationId, quantity: Number(quantity) }));
      setDenominationId(""); setQuantity(""); onCreated(created);
    } catch (caught) { setError(asApiError(caught)); }
    finally { setBusy(false); }
  };
  return <Modal open={open} title={t("pages.vouchers.createBatch")} onClose={onClose}><ApiErrorAlert error={error} /><form className="form-grid" onSubmit={(event) => void submit(event)}>
    <Field label={t("pages.vouchers.denomination")}><Select required value={denominationId} onChange={(event) => setDenominationId(event.target.value)}><option value="">{t("common.select")}</option>{denominations.filter((item) => item["status"] === "active").map((item) => <option key={stringValue(item["publicId"])} value={stringValue(item["publicId"])}>{stringValue(item["name"])} — {stringValue(item["credits"])} {t("pages.vouchers.credits")}</option>)}</Select></Field>
    <Field label={t("pages.vouchers.quantity")} hint={t("pages.vouchers.quantityHint")}><Input required type="number" min="1" max="10000" step="1" value={quantity} onChange={(event) => setQuantity(event.target.value)} /></Field>
    <Button type="submit" busy={busy} disabled={denominationId === ""}>{t("pages.vouchers.generate")}</Button>
  </form></Modal>;
}
